// src/context/AuthContext.jsx
import { createContext, useState, useEffect, useMemo, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase/config";
import apiService from "../services/api.service";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función para refrescar el token
  const refreshToken = useCallback(async (user) => {
    if (!user) return null;
    try {
      const freshToken = await user.getIdToken(true);
      setToken(freshToken);
      return freshToken;
    } catch (err) {
      console.error("Error refreshing token:", err);
      return null;
    }
  }, []);

  // Función para cargar datos del usuario
  const loadUserData = useCallback(async (user, idToken) => {
    try {

      if (user && !user.email) {
        console.log("👤 Usuario con solo teléfono, omitiendo carga de datos");
        setUserData({ 
          uid: user.uid,
          phoneNumber: user.phoneNumber,
        });
        setError(null);
        return null;
      }
      const data = await apiService.getCurrentUser();
      setUserData(data);
      setError(null);
      return data;
    } catch (err) {
      console.error("Error loading user data:", err);

      if (err.message.includes("No tienes permisos") && user && !user.email) {
      console.log("⚠️ Error esperado: usuario con solo teléfono");
      setUserData({ 
        uid: user.uid,
        phoneNumber: user.phoneNumber,
      });
      setError(null);
      return null;
    }

    if (err.message.includes("expirad") || err.message.includes("autenticación")) {
        setFirebaseUser(null);
        setToken(null);
        setUserData(null);
      }
    
      setError(err.message);
      // Si hay error 401, limpiar todo
      
      throw err;
    }
  }, []);
  
  // Listener de cambios de autenticación
  useEffect(() => {
    let mounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mounted) return;

      console.log("🔄 Auth state changed:", user?.email || "No user");
      
      apiService.clearTokenCache();

      if (!user) {
        setFirebaseUser(null);
        setToken(null);
        setUserData(null);
        setLoading(false);
        setError(null);
        return;
      }

      try {
        // Obtener token fresco
        const idToken = await user.getIdToken(true);
        
        if (!mounted) return;
        
        setFirebaseUser(user);
        setToken(idToken);

        // Cargar datos del usuario
        await loadUserData(user, idToken);
        
      } catch (err) {
        console.error("❌ Error en el proceso de autenticación:", err);
        if (mounted) {
          setError(err.message);
          // En caso de error, limpiar estado
          setFirebaseUser(null);
          setToken(null);
          setUserData(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [loadUserData]);

  // Refrescar token cada 50 minutos (Firebase tokens duran 1 hora)
  useEffect(() => {
    if (!firebaseUser) return;

    const interval = setInterval(async () => {
      console.log("🔄 Refrescando token...");
      await refreshToken(firebaseUser);
    }, 50 * 60 * 1000); // 50 minutos

    return () => clearInterval(interval);
  }, [firebaseUser, refreshToken]);

  // Función para recargar datos del usuario (útil después de actualizaciones)
  const reloadUserData = useCallback(async () => {
    if (!firebaseUser) return;
    try {
      setLoading(true);
      await loadUserData(firebaseUser, token);
    } catch (err) {
      console.error("Error reloading user data:", err);
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, token, loadUserData]);

  // Valor memoizado para optimizar re-renders
  const value = useMemo(() => ({
    firebaseUser, 
    token, 
    userData, 
    loading,
    error,
    isAuthenticated: !!firebaseUser && !!userData && !!token,
    refreshToken: () => refreshToken(firebaseUser),
    reloadUserData
  }), [firebaseUser, token, userData, loading, error, refreshToken, reloadUserData]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};